import { Component, ElementRef, Inject, Injector, OnDestroy, Renderer2, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FiDialogsContainerBase } from '@fi-shared/fi-dialogs/fi-dialogs-container-base';
import { IFiDialogContentOptions } from '@fi-shared/fi-dialogs/fi-dialogs';
import { findClosest } from '@fi-shared/utils';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';

@Component({
    selector: 'fi-common-two-buttons',
    templateUrl: './common-two-buttons.component.html',
    styleUrls: ['./common-two-buttons.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-common-two-buttons' },
    standalone: false,
})
export class FiDialogsCommonTwoButtonsComponent<TData, TRes>
    extends FiDialogsContainerBase<TData, TRes>
    implements OnDestroy
{
    private overlayEl: HTMLElement;
    private containerEl: HTMLElement;
    private renderer: Renderer2;
    private contentEl: HTMLElement;
    private contentChildEl: HTMLElement;

    private checkHeightOverflow$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) dialogData: IFiDialogContentOptions<TData, TRes>,
        dialogRef: MatDialogRef<FiDialogsContainerBase<TData, TRes>>,
        injector: Injector,
    ) {
        super(dialogData, dialogRef, injector);

        this.renderer = injector.get(Renderer2);

        if (!this.dialogData.disableHeightOverflowCheck) {
            this.checkHeightOverflow$
                .pipe(
                    debounce(() => interval(200)),
                    takeUntil(this.ngOnDestroy$),
                )
                .subscribe(() => this.checkHeightOverflow());

            const sub = dialogRef.afterOpened().subscribe(() => {
                const elementRef: ElementRef<HTMLElement> = injector.get(ElementRef);
                this.overlayEl = findClosest(elementRef.nativeElement, '.cdk-overlay-pane');
                this.containerEl = findClosest(elementRef.nativeElement, '.cdk-dialog-container');
                this.contentEl = elementRef.nativeElement.querySelector('.fi-dialog-content');
                this.contentChildEl = this.contentEl?.firstElementChild as HTMLElement;
                this.checkHeightOverflow$.next();
                sub.unsubscribe();

                if ('ResizeObserver' in window) {
                    const resizeObserver = new window.ResizeObserver(() => this.checkHeightOverflow$.next());
                    resizeObserver.observe(elementRef.nativeElement);
                }
            });

            injector
                .get(FiBrowserEventsService)
                .getWindowEventSubject$('resize')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => this.checkHeightOverflow$.next());
        }
    }

    protected getTopTileClass(): string {
        return this.dialogData.colorClass ?? 'BrandedBackgroundColor';
    }

    protected getDialogContentColorClass(): string {
        return this.dialogData.colorClass ?? '';
    }

    protected getNoButtonClass(): string {
        const classList = ['noButton', 'dialogs-button'];
        if (!this.isNoButtonPrimaryColor()) {
            classList.push(this.dialogData.colorClass ?? 'BrandedColor');
        }
        if (this.isNoButtonPrimaryColor()) {
            classList.push(this.dialogData.colorClass ?? 'BrandedBackgroundColor');
        }
        return classList.join(' ');
    }

    protected getYesButtonClass(): string {
        const classList = ['yesButton', 'dialogs-button'];
        if (!this.isYesButtonDisabled()) {
            if (!this.isNoButtonPrimaryColor()) {
                classList.push(this.dialogData.colorClass ?? 'BrandedBackgroundColor');
            }
            if (this.isNoButtonPrimaryColor()) {
                classList.push(this.dialogData.colorClass ?? 'BrandedColor');
            }
        }
        return classList.join(' ');
    }

    protected isYesIconButton(): boolean {
        return this.dialogData.isYesIconButton;
    }

    protected getYesButtonIconClass(): string {
        return this.dialogData.yesButtonIconClass;
    }

    private has100PrcHeight = false;
    private checkHeightOverflow() {
        // https://chainformation.atlassian.net/browse/FI-5300
        if (this.overlayEl && this.containerEl) {
            if (this.overlayEl.clientHeight < this.containerEl.clientHeight) {
                this.renderer.setStyle(this.overlayEl, 'height', '100%');
                this.has100PrcHeight = true;
            } else if (this.contentEl && this.contentChildEl && this.has100PrcHeight) {
                if (
                    this.contentChildEl.clientHeight < this.contentEl.clientHeight &&
                    this.dialogData.revertHeightChanges
                ) {
                    this.renderer.removeStyle(this.overlayEl, 'height');
                    this.has100PrcHeight = false;
                }
            }
        }
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
